import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {JSFeatureFlag} from '@github-ui/feature-flags/client-feature-flags'

import {getBannerRegistry} from '../marketing-global-banner.data'
import type {BannerDescriptor} from '../marketing-global-banner.types'
import {normalizeLocale} from './locale'

/** Exact path match (no wildcards) with allow/exclude semantics, mirroring the legacy `allowed_on_pages?`. */
function allowedOnPage(current_path: string | undefined, banner: BannerDescriptor): boolean {
  if (banner.allowed_paths && (current_path === undefined || !banner.allowed_paths.includes(current_path))) {
    return false
  }
  if (banner.excluded_paths && current_path !== undefined && banner.excluded_paths.includes(current_path)) {
    return false
  }
  return true
}

export type GetActiveBannerContext = {
  current_path?: string
  is_staff?: boolean
  locale?: string
}

export type GetActiveBannerDeps = {
  isEnabled?: (flag: string) => boolean
  banners?: BannerDescriptor[]
}

/**
 * Returns the first registry banner that passes its path, feature-flag, and `staff_only` checks, or
 * `null`. Deliberately does NOT read the dismissal cookie, so SSR output stays deterministic and
 * dismissal is left to the Catalyst controller at hydration.
 */
export function getActiveBanner(
  {current_path, is_staff, locale}: GetActiveBannerContext = {},
  {isEnabled = flag => isFeatureEnabled(flag as JSFeatureFlag), banners}: GetActiveBannerDeps = {},
): BannerDescriptor | null {
  const list = banners ?? getBannerRegistry(normalizeLocale(locale))
  const match = list.find(banner => {
    if (!isEnabled(banner.feature_flag)) return false
    if (banner.staff_only && !is_staff) return false
    return allowedOnPage(current_path, banner)
  })
  return match ?? null
}
